interface LegalSection {
  heading: string;
  paragraphs?: string[];
  items?: string[];
}

const pages: Record<string, { title: string; intro: string; sections: LegalSection[] }> = {
  rules: {
    title: "RULES",
    intro:
      "Version 1.1.0. These economic rules become locked when the first live press is captured.",
    sections: [
      {
        heading: "Price",
        items: [
          "The minimum starts at $1 USD.",
          "A buyer can choose any whole-dollar amount at or above the current minimum.",
          "After capture, the next minimum is the captured subtotal plus $1.",
          "Taxes, if activated, are separate and never raise the minimum.",
          "Refunds, disputes, and moderation never reduce the minimum.",
        ],
      },
      {
        heading: "Concurrent presses",
        paragraphs: [
          "The first valid authorization to atomically claim the current Durable Object version wins. The highest amount does not win automatically.",
          "Losing authorizations are not captured and are cancelled. A bank may temporarily show an authorization while it is released.",
        ],
      },
      {
        heading: "What a buyer receives",
        items: [
          "Immediate featured homepage placement until replaced.",
          "A permanent numbered history page.",
          "A display name, short message, optional destination link, and buyer-selected site-wide color theme.",
          "While the press is current, its color controls the main button, links, highlights, and site surfaces.",
          "Aggregated outbound click statistics without a promise that clicks are human.",
        ],
      },
      {
        heading: "What is not included",
        paragraphs: [
          "There is no prize, investment, ownership interest, token, resale right, revenue share, guaranteed duration, guaranteed traffic, or guaranteed number of clicks.",
        ],
      },
    ],
  },
  terms: {
    title: "TERMS",
    intro: "Effective 21 August 2026. These terms govern purchases made on dontpress.lol.",
    sections: [
      {
        heading: "Nature of the service",
        paragraphs: [
          "Don't Press sells an immediate digital display placement and site-wide color theme on dontpress.lol plus a permanent numbered history page. Featured placement and color control can be replaced immediately and have no guaranteed duration.",
        ],
      },
      {
        heading: "Eligibility and consent",
        paragraphs: [
          "Buyers must be at least 18, have legal capacity to purchase the service, accept the current policies, and explicitly request immediate service after capture.",
        ],
      },
      {
        heading: "Buyer content",
        paragraphs: [
          "The buyer confirms that submitted content is lawful and that they have the rights needed to provide it. The buyer grants a limited license to display and archive that content for this service.",
        ],
      },
      {
        heading: "Operator and contact",
        items: [
          "Operator: Cristian Lazaro Peris, acting as an individual entrepreneur (empresario individual).",
          "Postal address: Carrer D'Antoninus Pius 141, 08224 Terrassa, Barcelona, Spain.",
          "Support and legal contact: cristianlazaro@gmail.com.",
        ],
      },
      {
        heading: "Governing law",
        paragraphs: [
          "Spanish law governs these terms. This choice does not deprive consumers of mandatory protections or jurisdiction rights available under applicable law.",
        ],
      },
    ],
  },
  privacy: {
    title: "PRIVACY",
    intro: "Effective 21 August 2026. This notice explains how dontpress.lol handles data.",
    sections: [
      {
        heading: "Controller and contact",
        paragraphs: [
          "The data controller is Cristian Lazaro Peris, an individual entrepreneur at Carrer D'Antoninus Pius 141, 08224 Terrassa, Barcelona, Spain. Privacy requests can be sent to cristianlazaro@gmail.com.",
        ],
      },
      {
        heading: "Data used",
        paragraphs: [
          "The service processes submitted public content, payment references, consent timestamps, operational event IDs, aggregate click counts, and short-lived abuse-prevention identifiers. It does not store card numbers, CVC, passwords, or full IP addresses.",
        ],
      },
      {
        heading: "Providers",
        paragraphs: [
          "Stripe processes payment and receipt data. Cloudflare provides hosting, security, abuse prevention, and optional cookieless analytics. Each provider handles data under its own terms and privacy commitments.",
        ],
      },
      {
        heading: "Purpose and retention",
        paragraphs: [
          "Data is used to deliver the purchased display, maintain the permanent history, prevent abuse, reconcile payments, handle disputes, and meet legal obligations. Full webhook bodies are not retained as an event archive.",
        ],
      },
      {
        heading: "Your rights",
        paragraphs: [
          "Applicable access, correction, deletion, restriction, portability, objection, and complaint rights remain available. Contact cristianlazaro@gmail.com to make a request. You may also complain to the Spanish Data Protection Agency or another competent supervisory authority.",
        ],
      },
    ],
  },
  refunds: {
    title: "REFUND POLICY",
    intro: "No absolute no-refunds rule is used. Mandatory consumer rights remain unaffected.",
    sections: [
      {
        heading: "Immediate service",
        paragraphs: [
          "The digital display service begins when payment is captured. Being replaced by a later valid press is part of the service and is not by itself a technical failure.",
        ],
      },
      {
        heading: "Issues we investigate",
        items: [
          "Verified duplicate charges.",
          "A captured payment that did not create the purchased display or history page.",
          "A material technical failure in service delivery.",
          "Requests required by mandatory consumer law.",
        ],
      },
      {
        heading: "After a refund",
        paragraphs: [
          "The press number and neutral historical record remain. A refund never lowers the current minimum. If the refunded press was current, the latest eligible holder is restored.",
        ],
      },
      {
        heading: "Contact",
        paragraphs: [
          "Send refund requests to cristianlazaro@gmail.com with the press number and Stripe payment reference. Do not send card details.",
        ],
      },
    ],
  },
  "acceptable-use": {
    title: "ACCEPTABLE USE",
    intro:
      "Public content and destination links must be lawful, truthful, safe, and owned or authorized by the buyer.",
    sections: [
      {
        heading: "Prohibited content",
        items: [
          "Illegal content, malware, phishing, scams, piracy, or malicious downloads.",
          "Impersonation, threats, targeted harassment, hate, or personal data posted without permission.",
          "Sexual exploitation, pornography, or content directed at minors.",
          "Illegal products, weapons, drugs, gambling, or financial services.",
          "Obvious intellectual-property infringement, deceptive advertising, official-entity impersonation, URL shorteners, or misleading redirects.",
        ],
      },
      {
        heading: "Moderation",
        paragraphs: [
          "Text can be hidden, a link can be disabled, and a press can be disqualified from featured placement. Actions are audited. The number and permanent page remain with a neutral notice.",
        ],
      },
    ],
  },
  faq: {
    title: "FAQ",
    intro: "The short version: a paid digital display, one global state, no prize.",
    sections: [
      {
        heading: "What am I buying?",
        paragraphs: [
          "Immediate featured homepage placement and site-wide color control until a later valid press replaces you, plus a permanent numbered history page and aggregate link statistics.",
        ],
      },
      {
        heading: "How does the price work?",
        paragraphs: [
          "The displayed amount is the current minimum. You can choose that amount or a higher whole-dollar amount. After capture, your subtotal plus $1 becomes the next minimum.",
        ],
      },
      {
        heading: "How long will I stay on the homepage?",
        paragraphs: ["There is no guaranteed duration. Another buyer can replace you immediately."],
      },
      {
        heading: "What happens if someone presses first?",
        paragraphs: [
          "Only the first valid authorization to claim the current version is captured. Losing authorizations are cancelled and create no history entry.",
        ],
      },
      {
        heading: "Why does my bank show a temporary authorization?",
        paragraphs: [
          "A bank can show a hold before Stripe captures or cancels it. Release timing is controlled by the bank.",
        ],
      },
      {
        heading: "Can I edit my press?",
        paragraphs: ["No. Review the preview carefully before opening Checkout."],
      },
      {
        heading: "Is this a game, prize, investment, or traffic guarantee?",
        paragraphs: ["No. None of those are part of the service."],
      },
      {
        heading: "What happens to my history page?",
        paragraphs: [
          "Its number and URL remain. Prohibited content can be replaced with a neutral moderation notice.",
        ],
      },
      {
        heading: "How do I report content?",
        paragraphs: ["Email cristianlazaro@gmail.com with the affected URL and a clear reason."],
      },
    ],
  },
  security: {
    title: "SECURITY",
    intro: "Responsible security reports are welcome.",
    sections: [
      {
        heading: "What to include",
        items: [
          "The affected URL and a clear impact description.",
          "Minimal, reproducible steps that do not access other people's data.",
          "No public disclosure before a reasonable remediation window.",
        ],
      },
      {
        heading: "Contact",
        paragraphs: [
          "Send security reports to cristianlazaro@gmail.com. Do not include passwords, payment-card data, or personal data that is not necessary to demonstrate the issue.",
        ],
      },
    ],
  },
};

export function LegalPage({ page }: { page: string }) {
  const content = pages[page] ?? pages.rules;
  if (!content) return null;
  return (
    <main className="content-page legal-page">
      <header className="page-intro">
        <p className="section-kicker">DON'T PRESS</p>
        <h1>{content.title}</h1>
        <p>{content.intro}</p>
      </header>
      <div className="legal-sections">
        {content.sections.map((section) => (
          <section key={section.heading}>
            <h2>{section.heading}</h2>
            {section.paragraphs?.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            {section.items ? (
              <ul>
                {section.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
      </div>
      <aside className="legal-warning">
        These operational policies do not limit mandatory statutory rights. The operator should
        obtain qualified legal and tax advice for its specific circumstances.
      </aside>
    </main>
  );
}
